import React from "react";
import { FragmentProvider } from "core/components/FragmentProvider";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { FragmentLayout } from "../../core/components/FragmentLayout";
import IKBFragmentProps from "./fragments/article/IKBFragementProps";
import * as itemContainerFragments from "./fragments/container/itemContainerFragments";
import * as displayFragments from "./fragments/article/displayFragments";
import * as miscFragments from "./fragments/generalFragments";
import * as parentContainerFragments from "./fragments/container/parentContainerFragments";
import Article from "./entities/Article";
import { IKbMetaProps } from "./fragments/IKbMetaProps";
import KnowledgeBaseNewPanelConfig from "./config/KnowledgeBaseNewPanelConfig";
import KbLabels from "./Kb.labels";
import { articleParamNames, IArticleParams } from "./entities/ArticleParams";
import { IQueryRoot, QueryRoot } from "core/decorators/QueryRoot";
import { Field, Operation, Entity } from "typegraph";
import CountResult from "common/entities/CountResult";
import initialState from "core/reducers/initialState";
import StringUtil from "core/util/StringUtil";
import * as filterFragments from "./fragments/filterFragments";
import KnowledgeBase from "panels/knowledgebase/entities/KnowledgeBase";

function mapQueryParams(args: any) {
	const params = {};

	Object.entries(args).forEach(([key, value]) => {
		if (value) {
			params[key] = value;
		}
	});

	return { params };
}

@Operation({ type: "query", name: "getArticleList" })
class ArticleQuery {
	@Field({
		entity: Article,
		aliasFor: "article_many",
		params: mapQueryParams
	})
	public article: null | Article[];

	@Field({
		entity: CountResult,
		aliasFor: "article_count",
		params: mapQueryParams
	})
	public count: CountResult;

	@Field({
		entity: KnowledgeBase,
		aliasFor: "knowledgeBase_one",
		params: { params: {} }
	})
	public knowledgeBase: KnowledgeBase;
}
// panel level fragments for intro and filtering
const generalFragments = {
	...filterFragments,
	...miscFragments
};

// what type of list
const ContainerFragments = FragmentProvider.forFragments(
	"article-container",
	parentContainerFragments
);

// what type of article wrapper
const ItemFragments = FragmentProvider.forFragments(
	"article-item",
	itemContainerFragments
);

// the info available for a article
const articleFragments = {
	...displayFragments
};

function mergeConfigAndParams(
	config: KnowledgeBaseNewPanelConfig,
	pageParams: { [key: string]: any },
	user: typeof initialState.user
) {
	const p: IArticleParams = {};
	for (const [key, value] of Object.entries({
		...(config.preselectedParams || {}),
		...pageParams
	})) {
		if (!articleParamNames.includes(key)) {
			continue;
		}
		if (key === "pageSize") {
			p[key] = StringUtil.toInteger(value, 10);
			continue;
		}

		if (key === "sort") {
			p[key] = value;
			continue;
		}

		if (key === "pageNumber") {
			p[key] = StringUtil.toInteger(value, 0);
			continue;
		}

		p[key] = value;
	}

	if (pageParams && pageParams.categoryId && pageParams.categoryId.length) {
		p["categoryIds"] = pageParams.categoryId.split(",");
	}

	return p;
}

@Panel({
	id: "knowledgebase-article-list",
	configClass: KnowledgeBaseNewPanelConfig,
	pageParams: true
})
@QueryRoot(ArticleQuery, mergeConfigAndParams)
class KnowledgeBaseArtcleListPanel extends React.Component<
	IPanelProps<KnowledgeBaseNewPanelConfig> &
		IQueryRoot<ArticleQuery, IArticleParams>,
	{ container: string }
> {
	public state = { container: null };
	private renderItem = (item: Article, index: number) => {
		const { config, queryParams, queryData } = this.props;
		const { knowledgeBase } = queryData || ({} as ArticleQuery);

		const articleProps: IKBFragmentProps = {
			article: item,
			params: queryParams,
			knowledgeBase: knowledgeBase
		};
		const itemKey = (item && item.id) || "article-" + index;

		return (
			<ItemFragments
				fragments={config.itemFragment}
				fragmentProps={{ key: itemKey }}
			>
				<FragmentLayout
					layout={config.kbFragmentsLayout}
					fragments={articleFragments}
					props={articleProps}
				/>
			</ItemFragments>
		);
	};

	private setParams = (newParams: IArticleParams) => {
		const { actions } = this.props;

		actions.pushPageParams(
			...Object.entries(newParams).map(([key, value]) => ({ key, value }))
		);
	};
	private replaceParams = (newParams: IArticleParams) => {
		const { actions } = this.props;

		actions.replacePageParams(
			...Object.entries(newParams).map(([key, value]) => ({ key, value }))
		);
	};

	public render() {
		const { config, queryParams, queryData, queryState } = this.props;
		const { article, count, knowledgeBase } =
			queryData || ({} as ArticleQuery);
		let resultsElement;
		if (!queryState.loading && (article == null || article.length === 0)) {
			resultsElement = <KbLabels.Labels.NoResults />;
		} else {
			const articleItemElements =
				Array.isArray(article) && article.map(this.renderItem);
			resultsElement = (
				<ContainerFragments
					fragments={this.state.container || config.containerFragment}
					fragmentProps={{
						article,
						knowledgeBase,
						renderItem: this.renderItem,
						params: queryParams,
						articleFragments: Object.values(
							articleFragments
						) as any,
						children: articleItemElements
					}}
				/>
			);
		}

		const generalProps: IKbMetaProps = {
			loading: false,
			params: this.props.pageParams,
			setParams: this.setParams,
			replaceParams: this.replaceParams,
			queryMeta: {
				count: article ? article.length : 0,
				pageNumber: queryParams.pageNumber || 0,
				pageSize: queryParams.pageSize,
				total: (count && count.count) || 0
			},
			viewOptions: {
				container: this.state.container || config.containerFragment,
				setContainer: container => this.setState({ container })
			},
			resultsElement
		};

		return (
			<FragmentLayout
				layout={config.mainLayout}
				fragments={{ ...generalFragments }}
				props={{ ...generalProps }}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/panels/knowledgebase/KnowledgeBaseNew.panel.tsx